import React, { useState, useEffect, useRef } from "react";
import { getUserBrain, createBrain, updateBrain, deleteBrain, addBrainEntry, updateBrainEntry, deleteBrainEntry, getBrainCompletionStatus } from "../services/api";
import { isAuthenticated } from "../utils/auth";
import styles from "../css/shared.module.css";
import { FiPlus, FiEdit, FiTrash2, FiCpu, FiFolder, FiFileText, FiImage, FiAlertTriangle } from "react-icons/fi";
import BrainSectionModal from "../components/BrainSectionModal";
import BrainEntryModal from "../components/BrainEntryModal";
import defaultBrainConfig from "../resources/defaultBrainConfig.json";

const BrainPage = () => {
  const [brains, setBrains] = useState([]);
  const [selectedBrain, setSelectedBrain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showBrainModal, setShowBrainModal] = useState(false);
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [editingBrain, setEditingBrain] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [isLoadingDefault, setIsLoadingDefault] = useState(false);
  const hasLoadedDefaultRef = useRef(false);
  const isLoadingDefaultRef = useRef(false);
  const [brainCompletionStatus, setBrainCompletionStatus] = useState(null);

  // Cargar Brain del usuario
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchBrain = async () => {
      try {
        setLoading(true);
        setError("");
        
        const response = await getUserBrain();
        if (response.success) {
          const userBrains = response.data.brains || [];
          console.log("Brains encontrados:", userBrains.length);
          
          // Si no hay brains y no se ha cargado la preconfiguración, cargar la preconfiguración
          if (userBrains.length === 0 && !hasLoadedDefaultRef.current && !isLoadingDefaultRef.current) {
            console.log("No hay brains, cargando preconfiguración...");
            isLoadingDefaultRef.current = true;
            hasLoadedDefaultRef.current = true;
            setIsLoadingDefault(true);
            await loadDefaultConfiguration();
          } else {
            setBrains(userBrains);
            // Auto-seleccionar el primer Brain si hay alguno disponible
            if (userBrains.length > 0) {
              console.log("Auto-seleccionando el primer Brain:", userBrains[0].title);
              setSelectedBrain(userBrains[0]);
            }
            
            // Cargar estado de completitud del Brain
            loadBrainCompletionStatus();
          }
        } else {
          setError(response.message);
        }
      } catch (err) {
        setError("Error al cargar el Brain");
        console.error("Error fetching brain:", err);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated()) {
      fetchBrain();
    } else {
      setError("No estás autenticado. Por favor, inicia sesión.");
      setLoading(false);
    }
  }, []); // Solo se ejecuta una vez al montar

  // Cargar estado de completitud del Brain
  const loadBrainCompletionStatus = async () => {
    try {
      const status = await getBrainCompletionStatus();
      if (status.success) {
        setBrainCompletionStatus(status.data);
      }
    } catch (err) {
      console.error("Error loading brain completion status:", err);
    }
  };

  // Cargar configuración por defecto
  const loadDefaultConfiguration = async () => {
    try {
      console.log("=== INICIANDO CARGA DE CONFIGURACIÓN POR DEFECTO ===");
      
      // Pequeño delay para evitar condiciones de carrera
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Verificar si ya existe un brain con el mismo título
      const existingResponse = await getUserBrain();
      if (existingResponse.success) {
        const existingBrains = existingResponse.data.brains || [];
        console.log("Brains existentes encontrados:", existingBrains.length);
        
        const existingBrain = existingBrains.find(brain => brain.title === defaultBrainConfig.brainTitle);
        
        if (existingBrain) {
          console.log("Ya existe un brain con el mismo título, no se creará duplicado");
          console.log("Brain existente:", existingBrain.title, "con", existingBrain.entries.length, "entradas");
          setBrains(existingBrains);
          isLoadingDefaultRef.current = false;
          setIsLoadingDefault(false);
          return;
        }
      }
      
      // Crear el brain principal
      console.log("Creando brain:", defaultBrainConfig.brainTitle);
      const brainResponse = await createBrain(
        defaultBrainConfig.brainTitle, 
        defaultBrainConfig.brainDescription
      );
      
      if (brainResponse.success) {
        const newBrain = brainResponse.data;
        console.log("Brain creado exitosamente con ID:", newBrain._id);
        
        // Crear todas las entradas del brain
        let entriesCreated = 0;
        for (const entryData of defaultBrainConfig.entries) {
          console.log(`Creando entrada ${entriesCreated + 1}/${defaultBrainConfig.entries.length}:`, entryData.title);
          
          const entryResponse = await addBrainEntry(
            newBrain._id,
            entryData.title,
            entryData.type,
            entryData.description,
            entryData.imageData
          );
          
          if (entryResponse.success) {
            entriesCreated++;
            console.log(`✓ Entrada creada exitosamente: ${entryData.title}`);
          } else {
            console.error(`✗ Error al crear entrada ${entryData.title}:`, entryResponse.message);
          }
        }
        
        console.log(`Total de entradas creadas: ${entriesCreated}/${defaultBrainConfig.entries.length}`);
        
        // Recargar los brains para obtener la estructura completa
        const finalResponse = await getUserBrain();
        if (finalResponse.success) {
          const finalBrains = finalResponse.data.brains || [];
          setBrains(finalBrains);
          // Auto-seleccionar el Brain recién creado
          if (finalBrains.length > 0) {
            console.log("Auto-seleccionando el Brain recién creado:", finalBrains[0].title);
            setSelectedBrain(finalBrains[0]);
          }
          console.log("=== PRECONFIGURACIÓN CARGADA EXITOSAMENTE ===");
        }
      } else {
        console.error("Error al crear brain:", brainResponse.message);
        setError("Error al crear la configuración inicial");
      }
      
    } catch (err) {
      console.error("Error al cargar la configuración por defecto:", err);
      setError("Error al cargar la configuración inicial");
    } finally {
      isLoadingDefaultRef.current = false;
      setIsLoadingDefault(false);
    }
  };

  // Crear nuevo Brain
  const handleCreateBrain = async (title, description) => {
    try {
      const response = await createBrain(title, description);
      console.log("handleCreateBrain - response:", response);
      if (response.success) {
        console.log("handleCreateBrain - response.data:", response.data);
        console.log("handleCreateBrain - response.data._id:", response.data._id);
        // Actualizar la lista de brains
        setBrains(prev => [...prev, response.data]);
        // Seleccionar automáticamente el nuevo brain
        setSelectedBrain(response.data);
        console.log("handleCreateBrain - selectedBrain set to:", response.data);
        setShowBrainModal(false);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError("Error al crear el Brain");
      console.error("Error creating brain:", err);
    }
  };

  // Actualizar Brain
  const handleUpdateBrain = async (brainId, title, description) => {
    try {
      const response = await updateBrain(brainId, title, description);
      if (response.success) {
        // Actualizar el brain en la lista
        setBrains(prev => prev.map(brain => 
          brain._id === brainId ? response.data : brain
        ));
        setShowBrainModal(false);
        setEditingBrain(null);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError("Error al actualizar el Brain");
      console.error("Error updating brain:", err);
    }
  };

  // Eliminar Brain
  const handleDeleteBrain = async (brainId) => {
    console.log("handleDeleteBrain - brainId:", brainId);
    if (!window.confirm("¿Estás seguro de que quieres eliminar este Brain? Esta acción no se puede deshacer.")) {
      return;
    }

    try {
      const response = await deleteBrain(brainId);
      console.log("handleDeleteBrain - response:", response);
      if (response.success) {
        // Remover el brain de la lista
        setBrains(prev => prev.filter(brain => brain._id !== brainId));
        if (selectedBrain && selectedBrain._id === brainId) {
          setSelectedBrain(null);
        }
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError("Error al eliminar el Brain");
      console.error("Error deleting brain:", err);
    }
  };

  // Abrir modal para crear Brain
  const handleNewBrain = () => {
    setEditingBrain(null);
    setShowBrainModal(true);
  };

  // Abrir modal para editar Brain
  const handleEditBrain = (brain) => {
    setEditingBrain(brain);
    setShowBrainModal(true);
  };

  // Abrir modal para crear entrada
  const handleNewEntry = (brain) => {
    console.log("handleNewEntry - brain:", brain);
    console.log("handleNewEntry - brain._id:", brain?._id);
    if (!brain || !brain._id) {
      console.error("Brain inválido:", { brain, hasId: !!brain?._id });
      setError("Brain no válido para agregar entrada");
      return;
    }
    setSelectedBrain(brain);
    setEditingEntry(null);
    setShowEntryModal(true);
  };

  // Abrir modal para editar entrada
  const handleEditEntry = (brain, entry) => {
    console.log("handleEditEntry - brain:", brain);
    console.log("handleEditEntry - entry:", entry);
    console.log("handleEditEntry - brain._id:", brain?._id);
    console.log("handleEditEntry - entry._id:", entry?._id);
    
    if (!brain || !brain._id || !entry || !entry._id) {
      console.error("Datos inválidos para editar:", { 
        brain: brain?._id, 
        entry: entry?._id,
        brainExists: !!brain,
        entryExists: !!entry
      });
      setError("Datos de entrada o brain inválidos para editar");
      return;
    }
    setSelectedBrain(brain);
    setEditingEntry(entry);
    setShowEntryModal(true);
  };

  // Guardar entrada (crear o actualizar)
  const handleSaveEntry = async (entryData) => {
    try {
      console.log("handleSaveEntry - selectedBrain:", selectedBrain);
      console.log("handleSaveEntry - entryData:", entryData);
      
      if (!selectedBrain || !selectedBrain._id) {
        setError("No hay Brain seleccionado");
        return;
      }

      if (editingEntry) {
        // Modo edición
        const response = await updateBrainEntry(
          selectedBrain._id,
          editingEntry._id,
          entryData.title,
          entryData.type,
          entryData.content,
          entryData.imageData
        );
        if (response.success) {
          // Actualizar la entrada en el brain seleccionado
          setSelectedBrain(prev => ({
            ...prev,
            entries: prev.entries.map(entry => 
              entry._id === editingEntry._id ? response.data : entry
            )
          }));
          // Actualizar también en la lista de brains
          setBrains(prev => prev.map(brain => 
            brain._id === selectedBrain._id 
              ? { ...brain, entries: brain.entries.map(entry => 
                  entry._id === editingEntry._id ? response.data : entry
                )}
              : brain
          ));
          setShowEntryModal(false);
          setEditingEntry(null);
          // Recargar estado de completitud
          loadBrainCompletionStatus();
        } else {
          setError(response.message);
        }
      } else {
        // Modo creación
        const response = await addBrainEntry(
          selectedBrain._id,
          entryData.title,
          entryData.type,
          entryData.content,
          entryData.imageData
        );
        if (response.success) {
          // Agregar la nueva entrada al brain seleccionado
          setSelectedBrain(prev => ({
            ...prev,
            entries: [...(prev.entries || []), response.data]
          }));
          // Actualizar también en la lista de brains
          setBrains(prev => prev.map(brain => 
            brain._id === selectedBrain._id 
              ? { ...brain, entries: [...(brain.entries || []), response.data] }
              : brain
          ));
          setShowEntryModal(false);
          // Recargar estado de completitud
          loadBrainCompletionStatus();
        } else {
          setError(response.message);
        }
      }
    } catch (err) {
      setError("Error al guardar la entrada");
      console.error("Error saving entry:", err);
    }
  };

  // Eliminar entrada
  const handleDeleteEntry = async (brain, entry) => {
    console.log("handleDeleteEntry - brain:", brain);
    console.log("handleDeleteEntry - entry:", entry);
    if (!window.confirm("¿Estás seguro de que quieres eliminar esta entrada? Esta acción no se puede deshacer.")) {
      return;
    }

    if (!brain || !brain._id || !entry || !entry._id) {
      console.error("Datos inválidos:", { brain: brain?._id, entry: entry?._id });
      setError("Datos de entrada o brain inválidos");
      return;
    }

    try {
      const response = await deleteBrainEntry(brain._id, entry._id);
      console.log("handleDeleteEntry - response:", response);
      if (response.success) {
        // Remover la entrada del brain seleccionado
        if (selectedBrain && selectedBrain._id === brain._id) {
          setSelectedBrain(prev => ({
            ...prev,
            entries: prev.entries.filter(e => e._id !== entry._id)
          }));
        }
        // Actualizar también en la lista de brains
        setBrains(prev => prev.map(b => 
          b._id === brain._id 
            ? { ...b, entries: b.entries.filter(e => e._id !== entry._id) }
            : b
        ));
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError("Error al eliminar la entrada");
      console.error("Error deleting entry:", err);
    }
  };

  // Seleccionar Brain
  const handleSelectBrain = (brain) => {
    console.log("handleSelectBrain - brain:", brain);
    console.log("handleSelectBrain - brain._id:", brain?._id);
    setSelectedBrain(brain);
  };

  // Verificar si una entrada está incompleta
  const isEntryIncomplete = (entry) => {
    if (!brainCompletionStatus?.missing) return false;
    return brainCompletionStatus.missing.includes(entry.title);
  };

  if (loading) {
    return (
      <div>
        <h1 className={styles.title}>Cargando Brain...</h1>
        <p className={styles.subtitle}>
          Preparando tu espacio de conocimiento, por favor espera.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.chatPageHeader}>
        <div className={styles.chatPageTitle}>
          <h1 className={styles.title}>
            <FiCpu size={32} style={{ marginRight: "12px", verticalAlign: "middle" }} />
            Mi Cerebro
          </h1>
          <p className={styles.subtitle}>
            Organiza tu conocimiento en secciones y entradas personalizadas
          </p>
        </div>
        
        <div className={styles.chatPageActions} style={{ display: 'none' }}>
          <button
            onClick={handleNewBrain}
            className={`${styles.button} ${styles.primary}`}
            title="Nuevo Brain"
          >
            <FiPlus size={16} />
            <span>Nuevo Brain</span>
          </button>
        </div>
      </div>

      {error && (
        <div className={styles.errorMessage}>
          {error}
        </div>
      )}

      {/* Indicador de carga para preconfiguración */}
      {isLoadingDefault && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingMessage}>
            <p>Configurando tu Brain inicial...</p>
            <p className={styles.loadingSubtext}>Creando secciones y entradas por defecto</p>
          </div>
        </div>
      )}

      <div className={styles.chatPageLayout}>
        {/* Panel izquierdo: Lista de Brains - OCULTO */}
        <div className={styles.chatSidebar} style={{ display: 'none' }}>
          <div className={styles.brainSidebar}>
            <div className={styles.brainSidebarHeader}>
              <h3>Mi Cerebro</h3>
              <button 
                className={styles.newSectionButton}
                onClick={handleNewBrain}
                title="Nuevo Brain"
                style={{ display: 'none' }}
              >
                <FiPlus size={16} />
              </button>
            </div>
            
            <div className={styles.brainSectionsList}>
              {brains.length === 0 ? (
                <div className={styles.emptyState}>
                  <FiFolder size={48} />
                  <p>No hay Brains creados</p>
                  <button 
                    className={`${styles.button} ${styles.primary}`}
                    onClick={handleNewBrain}
                    style={{ display: 'none' }}
                  >
                    <FiPlus size={16} />
                    <span>Crear primer Brain</span>
                  </button>
                </div>
              ) : (
                brains.map((brain) => (
                  <div
                    key={brain._id}
                    className={`${styles.brainSectionItem} ${
                      selectedBrain && selectedBrain._id === brain._id ? styles.active : ""
                    }`}
                    onClick={() => handleSelectBrain(brain)}
                  >
                    <div className={styles.brainSectionContent}>
                      <div className={styles.brainSectionTitle}>
                        <FiFolder size={16} />
                        <span>{brain.title}</span>
                      </div>
                      {brain.description && (
                        <p className={styles.brainSectionDescription}>
                          {brain.description}
                        </p>
                      )}
                      <div className={styles.brainSectionStats}>
                        <span>{brain.entries?.length || 0} entradas</span>
                      </div>
                    </div>
                    
                    <div className={styles.brainSectionActions}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditBrain(brain);
                        }}
                        className={styles.button}
                        title="Editar Brain"
                      >
                        <FiEdit size={16} />
                        <span>Editar</span>
                      </button>
                      {/* Botón de eliminar Brain oculto */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteBrain(brain._id);
                        }}
                        className={styles.actionButton}
                        title="Eliminar Brain"
                        style={{ display: 'none' }}
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Panel principal: Contenido del Brain seleccionado - ANCHO COMPLETO */}
        <div className={styles.chatMain} style={{ width: '100%', maxWidth: 'none' }}>
          {selectedBrain ? (
            <div className={styles.brainSectionContent}>
              <div className={styles.brainSectionHeader}>
                <div className={styles.brainSectionInfo}>
                  <h2>
                    <FiFolder size={24} />
                    {selectedBrain.title}
                  </h2>
                  {selectedBrain.description && (
                    <p className={styles.brainSectionDescription}>
                      {selectedBrain.description}
                    </p>
                  )}
                </div>
                
                <div className={styles.brainSectionActions} style={{ display: 'none' }}>
                  <button
                    onClick={() => handleNewEntry(selectedBrain)}
                    className={`${styles.button} ${styles.primary}`}
                    title="Nueva entrada"
                  >
                    <FiPlus size={16} />
                    <span>Nueva Entrada</span>
                  </button>
                </div>
              </div>

              <div className={styles.brainEntriesList}>
                {selectedBrain.entries && selectedBrain.entries.length > 0 ? (
                  selectedBrain.entries.map((entry, index) => (
                    <div key={entry._id || `entry-${index}`} className={`${styles.brainEntryItem} ${isEntryIncomplete(entry) ? styles.incompleteEntry : ''}`}>
                      <div className={styles.brainEntryContent}>
                        <div className={styles.brainEntryHeader}>
                          <div className={styles.brainEntryTitle}>
                            <h4>{entry.title}</h4>
                            {isEntryIncomplete(entry) && (
                              <div className={styles.incompleteIndicator}>
                                <FiAlertTriangle size={14} />
                                <span>Incompleta</span>
                              </div>
                            )}
                          </div>
                          <div className={styles.brainEntryType}>
                            {entry.type === "text" ? (
                              <FiFileText size={16} />
                            ) : (
                              <FiImage size={16} />
                            )}
                            <span>{entry.type === "text" ? "Texto" : "Imagen"}</span>
                          </div>
                        </div>
                        
                        <div className={styles.brainEntryBody}>
                          {entry.type === "text" ? (
                            <p>{entry.content}</p>
                          ) : (
                            <div>
                              <img 
                                src={entry.imageData} 
                                alt={entry.content}
                                className={styles.brainEntryImage}
                              />
                              <p>{entry.content}</p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className={styles.brainEntryActions}>
                        <button
                          onClick={() => handleEditEntry(selectedBrain, entry)}
                          className={styles.button}
                          title="Editar entrada"
                          disabled={!selectedBrain || !selectedBrain._id}
                        >
                          <FiEdit size={16} />
                          <span>Editar</span>
                        </button>
                        {/* Botón de eliminar entrada oculto */}
                        <button
                          onClick={() => handleDeleteEntry(selectedBrain, entry)}
                          className={styles.actionButton}
                          title="Eliminar entrada"
                          disabled={!selectedBrain || !selectedBrain._id}
                          style={{ display: 'none' }}
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={styles.emptyState}>
                    <FiFileText size={48} />
                    <p>No hay entradas en este Brain</p>
                                        <button
                      className={`${styles.button} ${styles.primary}`}
                      onClick={() => handleNewEntry(selectedBrain)}
                    >
                      <FiPlus size={16} />
                      <span>Crear primera entrada</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Estado inicial - Mostrar mensaje de bienvenida */
            <div className={styles.welcomeState}>
              <div className={styles.welcomeIcon}>
                <FiCpu size={80} />
              </div>
              <h2>¡Bienvenido a tu Brain!</h2>
              <p className={styles.welcomeSubtext}>
                Organiza tu conocimiento creando Brains y agregando entradas de texto o imágenes
              </p>
              <div className={styles.welcomeActions}>
                <button
                  onClick={handleNewBrain}
                  className={`${styles.button} ${styles.primary}`}
                  style={{ display: 'none' }}
                >
                  <FiPlus size={20} />
                  <span>Crear primer Brain</span>
                </button>
                <p className={styles.welcomeHint}>
                  💡 Los Brains te ayudan a organizar tu conocimiento por temas
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal para crear/editar Brain */}
      <BrainSectionModal
        isOpen={showBrainModal}
        onClose={() => {
          setShowBrainModal(false);
          setEditingBrain(null);
        }}
        section={editingBrain}
        onSave={editingBrain ? handleUpdateBrain : handleCreateBrain}
      />

      {/* Modal para crear/editar entrada */}
      <BrainEntryModal
        isOpen={showEntryModal}
        onClose={() => {
          setShowEntryModal(false);
          setEditingEntry(null);
          // NO resetear selectedBrain para mantener el Brain visible
        }}
        section={selectedBrain}
        entry={editingEntry}
        onSave={handleSaveEntry}
      />
    </div>
  );
};

export default BrainPage;
